import { createApiClient, newIdempotencyKey, query, seg, type ApiClient, type ErrorDetail } from "@gweb/ui";
import type { Account, Contact, HandoffDetails, HistoryEntry, Location, Opportunity, Page, PipelineConfig, Evidence } from "./types.js";
// Type-only, and therefore erased: the pages below are the ones that own these record shapes, and a client method
// whose signature restated them would be a second copy to drift. Nothing here is imported at run time.
import type { AuditPage, AuditQuery } from "./pages/AuditLog.js";
import type { TerritoryAssignment, TerritoryDeactivation, TerritoryDefinition } from "./pages/Territories.js";
import type { ForecastPeriodRead, SnapshotPeriod } from "./pages/Forecast.js";
import type { Attainment, QuotaRead } from "./pages/Compensation.js";
import type { AuditExportBundle, AuditExportChainResult, AuditExportManifest, AuditExportRequest } from "./pages/AuditExport.js";
import type { AccessReviewDecision, AccessReviewRow, AccessReviewRun, ReviewPeriod } from "./pages/AccessReview.js";
import type { RoutingAssignBody, RoutingAssignment, RoutingDecision, RoutingPreviewBody, RoutingQueueRow } from "./pages/RoutingQueue.js";
// Tier 6.5.7 — the approval record, restated by the page that draws it. Type-only, so nothing of the server's
// module is pulled into the bundle: the shapes agree by hand, and the page is where the agreement is stated.
import type { Approval, ApprovalStatus } from "./pages/ApprovalInbox.js";

/** The error class the platform client throws, re-exported so a page imports it beside the calls that throw it. */
export { ApiError } from "@gweb/ui";
/** One per-field refusal, as the shared error envelope spells it: `field` is the canonical path, `reason` is the server's own sentence. */
export type { ErrorDetail } from "@gweb/ui";

/** SR-003: the four kinds of link. Every one points from the superior (from) to the subordinate (to). */
export type HierarchyRelationship = "parent_of" | "subsidiary_of" | "division_of" | "merged_into";

/** One account reached over a link: the other end, the kind of link, and when it took effect. */
export interface HierarchyLink {
  account_id: string;
  relationship: HierarchyRelationship;
  effective_at: string;
}

/** What the account page reads for one account: the links above it, the links below it, and the guard's verdict. */
export interface Hierarchy {
  account_id: string;
  parents: HierarchyLink[];
  children: HierarchyLink[];
  /** True when the links lead back to this account — the shape SR-003 refuses to let exist, so it must be explained. */
  cycle: boolean;
}

/**
 * The platform error envelope, structurally. `ApiError` already carries all of it, but a panel that has to mark the
 * one field the server refused cannot assert the class to get at it: it catches `unknown`, and a fake client in a
 * test throws a plain object of this shape rather than an `ApiError` instance.
 */
export interface ApiFailure {
  status: number;
  code: string;
  category: string;
  message: string;
  /** One entry per refused field, in the server's own spelling: `handoff_details.locations`, `products.0.qty`, `$`. */
  details: ErrorDetail[];
  requestId: string | null;
}

/** The caught value as the envelope, or `null` when it is not one — an aborted request, a bug in a page, a `TypeError`. */
export function asApiFailure(e: unknown): ApiFailure | null {
  if (typeof e !== "object" || e === null) return null;
  const f = e as Partial<ApiFailure>;
  if (typeof f.status !== "number" || typeof f.message !== "string") return null;
  return {
    status: f.status,
    code: typeof f.code === "string" ? f.code : `http_${f.status}`,
    category: typeof f.category === "string" ? f.category : "Internal",
    message: f.message,
    details: Array.isArray(f.details) ? f.details : [],
    requestId: typeof f.requestId === "string" ? f.requestId : null,
  };
}

/**
 * The per-field refusals on a caught error, in the order the server gave them.
 *
 * The Phase 7 editors are written against this rather than against `message`: a rejected handoff, a missing
 * decision-maker, a line item with no quantity each arrive as one detail naming the field that is wrong, so the
 * panel can mark that field instead of saying the whole form is wrong. An error with no details yields an empty
 * list, which is the honest answer for a failure that is not about a field at all.
 */
export function fieldErrors(e: unknown): ErrorDetail[] { return asApiFailure(e)?.details ?? []; }

/** True for the two statuses that answer in per-field refusals: a schema failure (400) and a business rule (422). */
export function isFieldRefusal(e: unknown): boolean {
  const status = asApiFailure(e)?.status;
  return status === 400 || status === 422;
}

// ---------------------------------------------------------------------------------------------
// Phase 7 — the commercial artefacts and the CPQ chain. Each wire shape below is the record `src/api.ts`
// answers with, restated here because `ui` is a separate package that cannot import the server's modules, and
// each request body is the store input the route's own schema narrows. The two can only be checked by reading
// both files, so each says which one it came from.
// ---------------------------------------------------------------------------------------------

/** An `Actor` over the wire: the four types `src/discovery.ts` accepts, no more. */
export interface DiscoveryActor { type: "user" | "agent" | "service" | "system"; id: string }

/** SR-013's field-level provenance: who recorded the value, where it was read, and how far it can be believed. */
export interface DiscoveryProvenance {
  recorded_by: DiscoveryActor; source_type: string; source_reference: string; observed_at: string;
  confidence: number; verification_state: string;
  agent?: { agent_id: string; task_id: string; model_version: string };
}

/** One `Discovery`. `version` and `supersedes_discovery_id` are the revision chain, oldest first. */
export interface Discovery {
  discovery_id: string; opportunity_id: string; account_id: string; captured_by: DiscoveryActor; captured_at: string;
  pain_points: string[]; unknowns: string[]; decision_process: string; decision_makers: string[];
  current_state?: string;
  timeline?: { target_date: string; milestones?: string[] };
  competition_known?: { competitors: string[] };
  provenance: DiscoveryProvenance;
  version?: number; supersedes_discovery_id?: string | null;
}

/** F-008's open holds: the CRM fields this record contradicts, held until Sales Ops reconciles them. */
export interface DiscoveryContradiction {
  hold_id: string; opportunity_id: string; discovery_id: string;
  conflicting_fields: string[]; confidence: number; detected_at: string;
}

/**
 * A `DiscoveryInput` plus the two things the route takes out of the payload's hands: `version` names a revision
 * and `reason` is why. `account_id` is required even though the opportunity already names its account — the store
 * refuses a mismatch, which is the point of asking for it. `captured_by` is stamped by the server when absent.
 */
export interface DiscoveryInput {
  account_id: string; pain_points: string[]; unknowns: string[]; decision_process: string; decision_makers: string[];
  provenance: DiscoveryProvenance;
  current_state?: string;
  timeline?: { target_date: string; milestones?: string[] };
  competition_known?: { competitors: string[] };
  captured_by?: DiscoveryActor;
  version?: number;
  reason?: string;
}

/**
 * A revision's body: the same blocks a first write carries, plus **the version the caller read**.
 *
 * `account_id` is not here and `version` is not either. A revision corrects what was learned on the record being
 * corrected, so the account is read off that record and the version is stated as `expected_version` — the one the
 * caller read, not the one being written. The store checks the second against the chain, so a caller whose read is
 * stale is refused `version_conflict` rather than quietly becoming version `n + 1` over somebody else's correction.
 */
export interface DiscoveryRevisionBody {
  expected_version: number; pain_points: string[]; unknowns: string[]; decision_process: string;
  decision_makers: string[]; provenance: DiscoveryProvenance;
  current_state?: string;
  timeline?: { target_date: string; milestones?: string[] };
  competition_known?: { competitors: string[] };
  captured_by?: DiscoveryActor;
  reason?: string;
}

/**
 * The edge `POST /v1/discoveries/:id/link-qualification` writes: a decision that rests on a discovery.
 *
 * The pair is the identity (`<discovery_id>#<qualification_id>`), so re-linking the same pair is the same edge and
 * comes back as it stands rather than written twice.
 */
export interface DiscoveryQualificationLink {
  link_id: string; discovery_id: string; qualification_id: string; opportunity_id: string;
  linked_by: DiscoveryActor; linked_at: string;
}

/** One criterion as the qualification editor records it. */
export interface QualificationCriteria {
  framework: string; criterion: string; result: string; evidence_ref?: string; notes?: string;
}

/** One `Qualification`. A reversal is a new record that supersedes the last, never an edit of it. */
export interface Qualification {
  qualification_id: string; opportunity_id: string; framework: string;
  criteria_evaluated: QualificationCriteria[]; decision: string; rationale: string; evidence_refs: string[];
  decided_by: DiscoveryActor; decided_at: string;
  version?: number; supersedes_qualification_id?: string | null;
}

/** A `QualificationInput` without the opportunity, which the path names. `unqualified` needs the ops permission. */
export interface QualificationInput {
  framework: string; criteria_evaluated: QualificationCriteria[]; decision: string;
  rationale: string; evidence_refs: string[]; decided_by?: DiscoveryActor;
}

/** A catalog bundle cited by id and product codes — never a copy of what the bundle contains (ADR-010). */
export interface SolutionBundle { bundle_id: string; product_codes: string[] }

/** One immutable version of a solution. `total` is what the recommended products come to. */
export interface SolutionVersion {
  solution_id: string; version: number; status: "draft" | "in_review" | "attached" | "rejected";
  created_by: string; created_at: string; linked_discovery_ids: string[]; recommended_products: string[];
  bundles: SolutionBundle[]; total: number; supersedes?: string; contract_template_ref?: string;
}

/** The envelope, so a reader holding one row already has the whole lineage. */
export interface Solution {
  solution_id: string; opportunity_id: string; current_version: number; versions: SolutionVersion[];
  doc_version?: number;
}

export interface SolutionPayload {
  linked_discovery_ids: string[]; recommended_products: string[];
  bundles?: SolutionBundle[]; contract_template_ref?: string;
}

/** The same payload plus the two arguments `addVersion` takes out of it: which solution, and why. */
export interface SolutionRevision extends SolutionPayload { solution_id: string; change_log: string }

/** One priced line, as both a proposal version and a quote version state it. */
export interface ProposalVersionLine { product_code: string; qty: number; unit_price: number }

export type ProposalStatus = "draft" | "in_review" | "sent" | "accepted" | "rejected" | "expired" | "superseded";

export interface ProposalVersion {
  proposal_id: string; version: number; status: ProposalStatus; created_by: string; created_at: string;
  sent_at?: string; valid_until?: string; change_log: string[]; solution_refs: string[];
  products: ProposalVersionLine[]; total: number; customer_artifact_hash?: string; pricing_scenario_ref?: string;
}

export interface Proposal {
  proposal_id: string; opportunity_id: string; current_version: number; versions: ProposalVersion[];
  doc_version?: number;
}

/** The whole payload again on a revision, never a patch: the store diffs against the version it supersedes. */
export interface ProposalPayload {
  change_log: string[]; products: ProposalVersionLine[];
  solution_refs?: string[]; valid_until?: string; customer_artifact_hash?: string; pricing_scenario_ref?: string;
}

export type QuoteStatus = "draft" | "approved" | "sent" | "accepted" | "rejected" | "expired" | "superseded";

export interface QuoteVersion {
  quote_id: string; version: number; status: QuoteStatus; created_by: string; created_at: string;
  sent_at?: string; valid_from: string; valid_until: string; change_log: string[];
  products: ProposalVersionLine[]; pricing_scenario_ref?: string; approval_ref?: string;
  total: number; currency: string; superseded_by?: string; superseded_at?: string;
  /**
   * The marks a rejection leaves on the version, and the last thing the desk said about it.
   *
   * They are never cleared: a version that can be priced again under a fresh grant is version `n + 1`, which
   * carries none of them. The status stays `draft` on a rejected version — the price did not change, the decision
   * about it did — so the reason is the only thing on the record that says what was refused.
   */
  rejected_reason?: string; rejected_by?: string; rejected_at?: string;
}

export interface Quote {
  quote_id: string; proposal_id: string | null; opportunity_id: string; current_version: number;
  versions: QuoteVersion[]; superseded_by?: string; superseded_at?: string; supersedes?: string;
  doc_version?: number;
}

/** The quote window is ISO 8601 here, unlike a proposal's `valid_until`, which is a calendar day. */
export interface QuotePayload {
  valid_from: string; valid_until: string; change_log: string[]; products: ProposalVersionLine[];
  currency: string; pricing_scenario_ref?: string;
}

/**
 * One version's window, read by the store rather than reconstructed by the page (F-012).
 *
 * `started` is the window having opened, `lapsed` is `valid_until` having passed, and `valid` is the two together.
 * The page's own reading of the same fields would be a second answer to the same question, and the one that matters
 * is the store's: it is the answer the send gate and the expiry sweep act on.
 */
export interface QuoteVersionValidity {
  quote_id: string; version: number; status: QuoteStatus; valid_from: string; valid_until: string;
  started: boolean; lapsed: boolean; valid: boolean;
}

/** `GET /v1/quotes/:id/valid`: the whole chain's windows, evaluated by the store. */
export interface QuoteValidity {
  quote_id: string; opportunity_id: string; proposal_id: string | null; current_version: number;
  /** The moment the windows were evaluated against: `at` when the caller pinned one, otherwise now. */
  evaluated_at: string;
  /** The version the send gate and the expiry sweep act on. */
  current: QuoteVersionValidity;
  /** Every version, oldest first — the order `listVersions` returns them in. */
  versions: QuoteVersionValidity[];
}

/** One priced line in a scenario: the share off list is a ratio, and `net_price` is what the scenario totals from. */
export interface ScenarioProduct {
  product_code: string; qty: number; list_price: number; discount_pct: number; net_price: number;
}

export interface PricingScenario {
  scenario_id: string; opportunity_id: string; supersedes?: string; name: string;
  created_by: string; created_at: string; valid_from: string; valid_until?: string;
  products: ScenarioProduct[]; total: number; weighted_total: number;
  catalog_version: string; bundle_ids: string[]; promoted_at?: string; promotion_approval_ref?: string;
  version?: number;
}

/** One row of `GET /v1/scenarios/:id/compare`: what this shape costs against the best one on the deal. */
export interface ScenarioComparisonRow {
  scenario_id: string; name: string; total: number; weighted_total: number;
  delta_vs_best: number; catalog_version: string; bundle_ids: string[];
}

export interface ScenarioCreateBody {
  name: string; valid_from: string; valid_until?: string; products: ScenarioProduct[]; bundle_ids?: string[];
}

/** Every field falls back to the parent's own value when it is omitted, so a variant may name only what changed. */
export interface ScenarioVariantBody {
  name: string; valid_from?: string; valid_until?: string; products?: ScenarioProduct[]; bundle_ids?: string[];
}

export type ContractStatus = "draft" | "sent" | "signed" | "executed" | "void" | "disputed";

export interface ContractSignatory { name: string; role: string; signed_at: string }

/**
 * A contract is a reference, not the document: the executed artefact lives with the e-signature platform.
 * `template_version` is pinned at draft and never re-pointed, which is what makes F-014 detectable at send.
 */
export interface Contract {
  contract_id: string; opportunity_id: string; proposal_id: string | null;
  template_version: string; template_ref: string; counterparty_entity: string;
  signatories: ContractSignatory[]; status: ContractStatus;
  created_at: string; created_by: string; sent_at?: string; executed_at?: string;
  supersedes_contract_id?: string; superseded_by?: string; customer_artifact_hash?: string;
  disputed_reason?: string; disputed_at?: string; version?: number;
}

export interface ContractDraftBody {
  template_ref: string; template_version: string; counterparty_entity: string;
  proposal_id?: string; signatories?: ContractSignatory[];
}

/**
 * `GET /v1/contracts/:id/template-validity`: the template registry's own answer for the pinned version (F-014).
 *
 * `current` is the registry's `isCurrent`, which is the same call the `send` and `execute` gates make — so a page
 * showing this verdict cannot disagree with the gate that would refuse. The pin itself is never re-pointed.
 */
export interface ContractTemplateValidity {
  contract_id: string; opportunity_id: string; template_ref: string; template_version: string;
  current: boolean; status: ContractStatus; evaluated_at: string;
}

/**
 * F-016's verdict, answered by `POST /v1/opportunities/:id/handoff/validate` on both outcomes: an incomplete
 * handoff is a legitimate thing for a seller to be holding, so it is a `200` with `verdict: "rejected"` and every
 * missing field named in `missing[]` rather than a refusal. `HandoffValidation` is the same contract in words.
 */
export interface HandoffValidation {
  valid: boolean; verdict: "valid" | "rejected";
  /** Sorted by field path, then reason. Empty when `valid`. */
  missing: ErrorDetail[];
  complete: boolean; fields_checked: string[]; validator_version: string; validator_run_id: string; checked_at: string;
}

/** The four artefacts AS-002 asks whether one deal really covers its products with. */
export const PACKAGE_ARTEFACTS = ["solution", "proposal", "quote", "contract"] as const;
export type PackageArtefact = (typeof PACKAGE_ARTEFACTS)[number];

export interface PackageArtefactRef {
  artefact: PackageArtefact; id: string | null; version: number | null; status: string | null;
}

/** One product's coverage: which of the four artefacts cover it, and which are the gap. */
export interface PackageProductCoverage {
  product_id: string; covered: boolean; missing: PackageArtefact[]; covers: Record<PackageArtefact, PackageArtefactRef | null>;
}

export interface PackageCoverage {
  opportunity_id: string; bundle_id: string | null; products: PackageProductCoverage[];
  covered: boolean; verdict: "complete" | "incomplete"; missing_count: number;
  validator_version: string; checked_at: string;
}

export interface PackageBundle {
  bundle_id: string; opportunity_id: string; catalog_ref: string; product_ids: string[];
  expansion: PackageProductCoverage[]; complete: boolean;
}

/** Per-product approvals, because an approval on one product says nothing about the next one. */
export interface PackageProductApproval {
  approval_id: string; subject_type: string; subject_id: string; status: string;
  standing: boolean; requested_at: string; decided_by: string | null; decided_at: string | null;
  granted_until: string | null;
}

export interface PackageProductApprovalView {
  product_id: string; approved: boolean; approvals: PackageProductApproval[];
}

export interface PackageApprovals {
  opportunity_id: string; bundle_id: string | null; products: PackageProductApprovalView[];
  all_approved: boolean; checked_at: string;
}

/** One event off a product's stream, as it was recorded — nothing is folded back into an earlier event. */
export interface PackageStreamEvent {
  seq: number; source_aggregate_type: string; source_aggregate_id: string;
  source_aggregate_seq: number; event: Record<string, unknown>;
}

export interface PackageEventStreams {
  opportunity_id: string; bundle_id: string | null;
  streams: { product_id: string; events: PackageStreamEvent[]; monotonic: boolean }[];
}

export const LOCATION_ROLLOUT_STATUSES = ["pending", "scheduled", "in_progress", "live", "failed"] as const;
export type LocationRolloutStatus = (typeof LOCATION_ROLLOUT_STATUSES)[number];

/** One `(location, product)` row. The rollout id is the store's own hash of all three parts. */
export interface LocationRollout {
  rollout_id: string; opportunity_id: string; location_id: string; product_id: string;
  rollout_status: LocationRolloutStatus; target_live_date: string; live_date: string | null;
  created_at: string; updated_at: string; version: number;
  location_label: string | null; failure_reason: string | null; failure_at: string | null;
}

export interface LocationRolloutPlan {
  opportunity_id: string; rollouts: LocationRollout[]; created: LocationRollout[]; updated: LocationRollout[];
}

/** `all_accepted` is false when the store refused some of the rows it was asked to move; the refusals say which. */
export interface LocationRolloutOutcome {
  rollout: LocationRollout; opportunity_id: string; from_status: LocationRolloutStatus | null;
  all_accepted: boolean; onboarding_completed: boolean;
}

/** AR-029: the rows past their target date that have not gone live, with the idempotency key to escalate on. */
export interface AtRiskLocation {
  rollout_id: string; opportunity_id: string; location_id: string; product_id: string;
  location_label: string | null; rollout_status: LocationRolloutStatus; target_live_date: string;
  days_overdue: number; risk_window: string; idempotency_key: string; rule: string;
}

export interface LocationPlanBody {
  entries: { location_id: string; product_id: string; target_live_date: string; location?: Location }[];
}

/** `product_id` is in the body rather than the path: one location legitimately carries several rows. */
export interface LocationStatusBody {
  product_id: string; rollout_status: LocationRolloutStatus;
  live_date?: string; reason?: string; source_event_id?: string;
}

export interface CompetitorRecord {
  competitor_id: string; opportunity_id: string; name: string; category: string;
  strengths: string[]; weaknesses: string[]; discovered_via: string; signal_id?: string;
  confidence: number; major: boolean; major_basis: string[];
  recorded_at: string; recorded_by: string; status: "recorded" | "approved" | "rejected";
  decided_at?: string; decided_by?: string; rejection_reason?: string;
}

/** AR-013's re-score trigger rides on the decision rather than being derived again by the caller. */
export interface CompetitorDecision {
  competitor: CompetitorRecord;
  rescore: {
    rule_id: string; opportunity_id: string; account_id: string | null; competitor_id: string;
    signal_class: string; signal_id: string | null; major_basis: string[];
    idempotency_key: string; observed_at: string; evidence_refs: string[]; correlation_id: string;
  } | null;
}

/** One screening candidate, judged against AR-013's major floor. `discovered_via` is `null` when it was never said. */
export interface BlinkerCandidate {
  name: string; name_key: string; category: string; confidence: number; major: boolean;
  major_basis: string[]; discovered_via: string | null; signal_id: string | null;
  strengths: string[]; weaknesses: string[];
}

export interface RecordCompetitorBody {
  name: string; category: string; discovered_via: string; confidence: number;
  strengths?: string[]; weaknesses?: string[]; signal_id?: string; major?: boolean;
}

export interface BlinkerDeriveBody {
  candidates: { name: string; category: string; confidence: number; major?: boolean; discovered_via?: string; signal_id?: string; strengths?: string[]; weaknesses?: string[] }[];
  major_confidence?: number;
}

export const CROSS_SELL_STATUSES = ["surfaced", "suppressed", "dismissed", "actioned"] as const;
export type CrossSellStatus = (typeof CROSS_SELL_STATUSES)[number];

/** A recommendation on one account's feed. It names the deal it fired on and changes nothing there. */
export interface CrossSellRecommendation {
  signal_id: string; account_id: string; owner_id: string;
  source_opportunity_id: string | null; source_signal_id: string | null; signal_source: string | null;
  trigger_reason: string; recommended_products: string[]; reasoning: string; evidence_refs: string[];
  raised_at: string; status: CrossSellStatus;
  conflict: { conflict_kind: string; reasons: string[]; rules_fired: string[]; customer_statement_ref: string | null } | null;
  dismissed_reason: string | null; dismissed_at: string | null; dismissed_by: string | null;
  expansion_id: string | null; actioned_at: string | null; actioned_by: string | null;
  surfaced_by: string; updated_at: string;
}

/** F-044: what the customer has stated, which is what a surfacing recommendation is suppressed against. */
export interface CustomerDirection {
  excluded_products?: string[]; excluded_topics?: string[]; wait_until?: string;
  requested_products?: string[]; statement_ref?: string;
}

export interface CrossSellSurfaceBody {
  trigger_reason: string; recommended_products: string[]; reasoning: string; evidence_refs: string[];
  source_opportunity_id?: string; owner_id?: string; signal_id?: string; raised_at?: string;
  customer_direction?: CustomerDirection;
}

export const EXPANSION_STATUSES = ["open", "accepted", "declined"] as const;
export type ExpansionStatus = (typeof EXPANSION_STATUSES)[number];

/** A new deal opened beside the active one. `source_opportunity_id` is the deal that triggered it, left untouched. */
export interface ExpansionOpportunity {
  expansion_id: string; opportunity_id: string; source_opportunity_id: string | null;
  account_id: string; owner_id: string; products: string[]; trigger_reason: string; evidence_refs: string[];
  status: ExpansionStatus; stage: string; contact_id: string | null;
  created_at: string; created_by: string; updated_at: string; signal_id?: string;
}

export interface ExpansionCreateBody {
  products: string[]; trigger_reason: string; evidence_refs: string[];
  source_opportunity_id?: string; owner_id?: string; contact_id?: string;
  name?: string; next_action?: string; next_action_due?: string;
}

export interface ExpansionUpdateBody { status?: ExpansionStatus; owner_id?: string }

export const ACTIVATION_STATUSES = ["not_ready", "ready", "confirmed"] as const;
export type ActivationStatus = (typeof ACTIVATION_STATUSES)[number];

/** One criterion as the Onboarding API posts it: a criterion of the definition in force. */
export interface CriterionResultBody { criterion: string; pass: boolean; evidence?: string }

export interface ActivationCriterion {
  criterion_id: string; description: string; required: boolean; met: boolean; evidence_ref: string | null;
}

/** Activation is per `(opportunity, product)`: a bundle's products go live on different days. */
export interface Activation {
  activation_id: string; opportunity_id: string; account_id: string; product_id: string;
  criterion_version: string; criteria: ActivationCriterion[]; unmet_criteria: string[]; status: ActivationStatus;
  closed_at: string | null; days_since_close: number | null;
  activated_at: string | null; confirmed_at: string | null; confirmed_by: string | null;
  source_event_id: string | null; created_at: string; updated_at: string;
}

export interface ActivationEvaluateBody {
  product_id: string; criterion_results?: CriterionResultBody[];
  criterion_version?: string; source_event_id?: string;
}

/** `source_event_id` is required: a confirmation cannot be replayed into a second activation without it. */
export interface ActivationConfirmBody {
  product_id: string; source_event_id: string; criterion_results?: CriterionResultBody[];
  activated_at?: string; criterion_version?: string;
}

/** A replayed confirmation is a `200` with `replayed: true`: the original record, nothing new written. */
export interface ActivationConfirmation {
  activation: Activation; activated_at: string; event_id: string; replayed: boolean;
}

export interface ActivationCompleted {
  opportunity_id: string; completed_at: string; event_id: string; replayed: boolean;
}

/** AR-011's trigger surface: the follow-up due a week before the target activation date. */
export interface ActivationFollowUp {
  rule: string; subject_type: string; subject_id: string; account_id: string; product_id: string | null;
  target_activation_date: string; due_at: string; idempotency_key: string; audit_event: string;
  stop_conditions: string[];
}

export interface ActivationCriterionDefinition {
  criterion_id: string; description: string; required: boolean; rule: string;
}

/** ADR-034's governance surface: the definition in force for a product, criteria included. */
export interface ActivationDefinition {
  product_id: string; criterion_version: string; jurisdiction: string;
  effective_from: string | null; effective_to: string | null;
  criteria: ActivationCriterionDefinition[];
}

export const ACTIVATION_FAILURE_STATUSES = ["open", "escalated", "resolved", "cancelled"] as const;
export type ActivationFailureStatus = (typeof ACTIVATION_FAILURE_STATUSES)[number];

export interface ActivationFailure {
  failure_id: string; opportunity_id: string; kind: "slip" | "cancel"; detected_at: string;
  expected_activation_date: string; days_overdue: number; reason: string; closed_at?: string;
  status: ActivationFailureStatus;
}

/** AR-029's own vocabulary for the slip one detection pass found, carried on that pass's answer. */
export interface SlipEscalation {
  rule: string; subject_type: string; subject_id: string; account_id: string | null; owner: string | null;
  risk_window: string; days_overdue: number; expected_activation_date: string; priority: string; due_at: string;
  reason: string; idempotency_key: string; notify: string[]; audit_event: string; stop_conditions: string[];
}

/**
 * AR-006's surface for the same slip. `null` at every stage past `10 Proposal`, which is what a Closed Won deal is:
 * §prerequisites limits the rule to `03..10`, so a won deal that is late is F-020's failure and not a stale one.
 */
export interface SlipStale {
  rule: string; opportunity_id: string; account_id: string | null; stage: string; owner: string | null;
  days_overdue: number; expected_activation_date: string; idempotency_key: string; audit_event: string;
  prerequisite_stages: string[]; stop_conditions: string[];
}

/** F-020's detection pass: idempotent on the plan date it measures, and it says which stop condition fired. */
export interface SlipDetection {
  opportunity_id: string; failure: ActivationFailure | null; stopped_by: string[]; already_detected: boolean;
  /** Both rule surfaces ride on the same answer, so a caller reads the pass once and applies the rules itself. */
  escalation?: SlipEscalation | null;
  stale?: SlipStale | null;
}

export interface EscalationTask {
  subject_type: string; subject_id: string; assigned_to: string; reason: string;
  priority: string; due_at?: string; created_by_rule: string; idempotency_key?: string;
}

export interface EscalationOutcome {
  failure: ActivationFailure;
  trigger: { rule: string; subject_type: string; subject_id: string; account_id: string | null; owner_id: string | null; risk_window: string; days_overdue: number; expected_activation_date: string; priority: string; due_at: string; reason: string; idempotency_key: string; notify: string[]; audit_event: string; stop_conditions: string[] };
  task: EscalationTask;
}

/** F-021: the store returns the `closed_lost` move `planMove` must be asked for. It does not close the deal itself. */
export interface PreActivationCancellationOutcome {
  failure: ActivationFailure;
  cancellation: {
    opportunity_id: string; account_id: string | null; from_stage: string; to_side_state: string;
    side_state: string; closed_reason: string; reason: string; requires_roles: string[];
    blocks: string[]; notify: string[]; cancellation_event_id: string; idempotency_key: string;
    occurred_at: string; post_sign: boolean; aggregate_seq: number;
  };
  events: unknown[]; replayed: boolean;
}

export interface CancelActivationBody {
  cancellation_event_id: string; reason: string; expected_activation_date: string;
}

export interface ProcessorChange {
  processor_change_id: string; opportunity_id: string;
  from_processor: string; to_processor: string; reason: string; volume_delta: number | null;
  effective_at: string; recorded_at: string; recorded_by: string;
  superseded_artifact_ids: string[]; status: "recorded" | "linked" | "superseded";
  proposal_id: string | null; quote_id: string | null; contract_id: string | null;
  idempotency_key?: string; superseded_at?: string;
  superseded_versions?: { artifact_type: string; artifact_id: string; required_version: number; change_log: string[] }[];
  contract_supersessions?: { artifact_type: string; contract_id: string; reason: string }[];
}

/**
 * `supersede` writes no Proposal, Quote or Contract version: it returns the deterministic description of the
 * versions that must be created, and each owning store performs its own.
 */
export interface ProcessorChangeSupersession {
  processor_change_id: string; opportunity_id: string; idempotency_key: string;
  from_processor: string; to_processor: string; effective_at: string; superseded_at: string;
  superseded_artifact_ids: string[]; required_versions: ProcessorChange["superseded_versions"];
  contract_supersessions: ProcessorChange["contract_supersessions"]; events: unknown[];
}

export interface ProcessorChangeBody {
  from_processor: string; to_processor: string; effective_at: string;
  reason?: string; volume_delta?: number; idempotency_key?: string;
}

export interface ProcessorChangeLinksBody { proposal_id?: string; quote_id?: string; contract_id?: string }

/**
 * What `POST /v1/routing/assign` answers with, as `src/routing.ts` holds it: the write that followed the
 * decision, and the trace id the decision stored.
 *
 * Restated here because `ui` is a separate package that cannot import the server's modules. The page reads a
 * flat assignment (`RoutingAssignment`), so the mapping from this shape to that one is the client's job — the
 * page draws one shape rather than two.
 */
export interface RoutingAssignAnswer {
  result: {
    assignment: {
      account_id: string;
      owner_id: string | null;
      assigned_by: string;
      assigned_at: string;
      rule_id_used: string | null;
      priority: number | null;
    };
  };
  trace_id: string | null;
}

/** The Sales API, one function per route. Every write takes the idempotency key of the action that caused it. */
export function makeApi(c: ApiClient) {
  const opt = (signal?: AbortSignal) => ({ signal });
  /**
   * Every write's options. The key is required on every POST, and the client sends it on a `PUT` as well, so the
   * two Phase 7 `PUT` routes the server marks idempotent carry one too — hence the explicit `signal`, which is
   * what keeps this object assignable to the client's `RequestOptions` rather than rejected as sharing nothing.
   */
  const w = (idempotencyKey: string) => ({ signal: undefined, idempotencyKey });
  /**
   * The audit read's query string. `src/api.ts` gives every filter a single-value schema (`field: str`,
   * `action: str`, `classification: enum`), so a query that named several is narrowed to its first rather than
   * joined into one value the server would refuse. `high_impact` and `limit` are booleans and numbers on the query
   * and strings on the wire, which the route's own schema narrows back.
   */
  const auditParams = (q: AuditQuery): Record<string, string | undefined> => {
    const one = (v: string | readonly string[] | undefined): string | undefined =>
      typeof v === "string" ? v : Array.isArray(v) ? v[0] : undefined;
    return {
      entity_type: q.entity_type, entity_id: q.entity_id, field: one(q.field),
      actor: q.actor, actor_type: q.actor_type, action: one(q.action),
      reason: q.reason, correlation_id: q.correlation_id, classification: one(q.classification),
      high_impact: q.high_impact === undefined ? undefined : String(q.high_impact),
      from: q.from, to: q.to, limit: q.limit === undefined ? undefined : String(q.limit), cursor: q.cursor,
    };
  };
  return {
    pipeline: (signal?: AbortSignal) => c.get<PipelineConfig>("/v1/pipeline", opt(signal)),

    listAccounts: (p: { after?: string | null } = {}, signal?: AbortSignal) => c.get<Page<Account>>(`/v1/accounts${query({ limit: 200, after: p.after })}`, opt(signal)),
    getAccount: (id: string, signal?: AbortSignal) => c.get<Account>(`/v1/accounts/${seg(id)}`, opt(signal)),
    createAccount: (body: Record<string, unknown>, key: string) => c.post<Account>("/v1/accounts", body, w(key)),

    listContacts: (accountId: string, signal?: AbortSignal) => c.get<{ data: Contact[] }>(`/v1/accounts/${seg(accountId)}/contacts`, opt(signal)),
    addContact: (accountId: string, body: Record<string, unknown>, key: string) => c.post<Contact>(`/v1/accounts/${seg(accountId)}/contacts`, body, w(key)),
    verifyContact: (accountId: string, contactId: string, key: string) => c.post<Contact>(`/v1/accounts/${seg(accountId)}/contacts/${seg(contactId)}/verify`, {}, w(key)),

    /** An account with no hierarchy record yet answers 404, which is an empty hierarchy rather than a failure. */
    getHierarchy: async (accountId: string, signal?: AbortSignal): Promise<Hierarchy | null> => {
      try {
        return await c.get<Hierarchy>(`/v1/accounts/${seg(accountId)}/hierarchy`, opt(signal));
      } catch (e) {
        if (asApiFailure(e)?.status === 404) return null;
        throw e;
      }
    },

    listOpportunities: (p: { account_id?: string; after?: string | null } = {}, signal?: AbortSignal) =>
      c.get<Page<Opportunity>>(`/v1/opportunities${query({ limit: 200, account_id: p.account_id, after: p.after })}`, opt(signal)),
    getOpportunity: (id: string, signal?: AbortSignal) => c.get<Opportunity>(`/v1/opportunities/${seg(id)}`, opt(signal)),
    history: (id: string, signal?: AbortSignal) => c.get<{ data: HistoryEntry[] }>(`/v1/opportunities/${seg(id)}/history`, opt(signal)),
    createOpportunity: (body: Record<string, unknown>, key: string) => c.post<Opportunity>("/v1/opportunities", body, w(key)),
    updateDetails: (id: string, body: { expected_version: number; owner?: string; products?: string[]; next_action?: string; next_action_due?: string; handoff_details?: HandoffDetails }) =>
      c.put<Opportunity>(`/v1/opportunities/${seg(id)}/details`, body),
    // Tier 6.9.4: `closed_lost_detail` says what happened beside the reason it explains — the competitor a
    // `lost-to-competitor` close names, the narrative, and the day the loss happened.
    transition: (id: string, body: { to_stage: string; reason?: string; evidence?: Evidence[]; closed_reason?: string; closed_lost_detail?: { competitor?: string; narrative?: string; loss_date?: string }; next_review_date?: string; expected_version: number }, key: string) =>
      c.post<{ opportunity: Opportunity; history: HistoryEntry }>(`/v1/opportunities/${seg(id)}/transition`, body, w(key)),

    // -------------------------------------------------------------------------------------------
    // Phase 7. Grouped by workstream, one method per route, in the order `src/api.ts` registers them.
    // Every listing is the whole version chain rather than the standing answer, because a corrected
    // finding is a new record that supersedes the last and a reader holding only the latest would not
    // know the earlier claim was ever made.
    // -------------------------------------------------------------------------------------------

    // Deal discovery (F-036 / F-037).
    listDiscovery: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: Discovery[] }>(`/v1/opportunities/${seg(opportunityId)}/discovery${query({ limit: p.limit })}`, opt(signal)),
    createDiscovery: (opportunityId: string, body: DiscoveryInput, key: string) =>
      c.post<Discovery>(`/v1/opportunities/${seg(opportunityId)}/discoveries`, body, w(key)),
    listDiscoveryContradictions: (opportunityId: string, signal?: AbortSignal) =>
      c.get<{ data: DiscoveryContradiction[] }>(`/v1/opportunities/${seg(opportunityId)}/discovery/contradictions`, opt(signal)),
    /**
     * 7.1.c's revision — a `PUT`, because the path names the deal and the body names the record being corrected.
     *
     * The body carries `expected_version`: the version the caller read. The store checks it against the chain before
     * anything is written, so a stale read is refused rather than quietly becoming version `n + 1` over somebody
     * else's correction — and the account is read off the record, so a revision can never move a discovery onto
     * another customer's account.
     */
    reviseDiscovery: (opportunityId: string, body: DiscoveryRevisionBody, key: string) =>
      c.put<Discovery>(`/v1/opportunities/${seg(opportunityId)}/discoveries`, body, w(key)),
    /**
     * 7.1.c's link: the decision that rests on this discovery, and why.
     *
     * The path names the discovery and the body names the decision; neither names the deal, because the store reads
     * the deal off the discovery and refuses a decision taken on another one.
     */
    linkDiscoveryQualification: (discoveryId: string, body: { qualification_id: string; reason?: string }, key: string) =>
      c.post<DiscoveryQualificationLink>(`/v1/discoveries/${seg(discoveryId)}/link-qualification`, body, w(key)),

    // Qualification decisions (SR-014). A re-decision is a new record that supersedes the last.
    listQualification: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: Qualification[] }>(`/v1/opportunities/${seg(opportunityId)}/qualification${query({ limit: p.limit })}`, opt(signal)),
    evaluateQualification: (opportunityId: string, body: QualificationInput, key: string) =>
      c.post<Qualification>(`/v1/opportunities/${seg(opportunityId)}/qualifications`, body, w(key)),

    // Solution bundle (AS-006 §Solution).
    listSolutions: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: Solution[] }>(`/v1/opportunities/${seg(opportunityId)}/solutions${query({ limit: p.limit })}`, opt(signal)),
    createSolution: (opportunityId: string, body: SolutionPayload, key: string) =>
      c.post<Solution>(`/v1/opportunities/${seg(opportunityId)}/solutions`, body, w(key)),
    /** A `PUT`, because the path addresses the opportunity and the body names the solution: a deal may carry several. */
    addSolutionVersion: (opportunityId: string, body: SolutionRevision, key: string) =>
      c.put<SolutionVersion>(`/v1/opportunities/${seg(opportunityId)}/solutions`, body, w(key)),
    /** ADR-010: attaching writes the proposal's product lines from this version's recommendation, at `unit_price: 0`. */
    attachSolutionToProposal: (solutionId: string, proposal_id: string, key: string) =>
      c.post<{ proposal_id: string; solution_version: number }>(`/v1/solutions/${seg(solutionId)}/attach-proposal`, { proposal_id }, w(key)),

    // Proposal (ADR-017). `create` answers the new id and version; the envelope's chain rides on `listProposals`.
    listProposals: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: Proposal[] }>(`/v1/opportunities/${seg(opportunityId)}/proposals${query({ limit: p.limit })}`, opt(signal)),
    createProposal: (opportunityId: string, body: ProposalPayload, key: string) =>
      c.post<{ proposal_id: string; version: number }>(`/v1/opportunities/${seg(opportunityId)}/proposals`, body, w(key)),
    /** The whole payload again, not a patch: the store diffs a revision against the version it supersedes. */
    addProposalVersion: (proposalId: string, body: ProposalPayload, key: string) =>
      c.post<ProposalVersion>(`/v1/proposals/${seg(proposalId)}/versions`, body, w(key)),
    sendProposal: (proposalId: string, key: string) =>
      c.post<ProposalVersion>(`/v1/proposals/${seg(proposalId)}/send`, {}, w(key)),
    /** F-013 is judged on the hash: a hash that does not match the rendered artefact is containment, not a typo. */
    acceptProposal: (proposalId: string, customer_artifact_hash: string, key: string) =>
      c.post<Proposal>(`/v1/proposals/${seg(proposalId)}/accept`, { customer_artifact_hash }, w(key)),
    rejectProposal: (proposalId: string, reason: string, key: string) =>
      c.post<Proposal>(`/v1/proposals/${seg(proposalId)}/reject`, { reason }, w(key)),

    // Quote (AS-006 §Pricing). `quoteForOpportunity` is the effective version — `at` asks which quote was live then.
    getQuote: (quoteId: string, signal?: AbortSignal) => c.get<Quote>(`/v1/quotes/${seg(quoteId)}`, opt(signal)),
    listQuoteVersions: (quoteId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: QuoteVersion[] }>(`/v1/quotes/${seg(quoteId)}/versions${query({ limit: p.limit })}`, opt(signal)),
    quoteForOpportunity: (opportunityId: string, p: { at?: string; limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: Quote | null }>(`/v1/opportunities/${seg(opportunityId)}/quote${query({ at: p.at, limit: p.limit })}`, opt(signal)),
    /** SR-017: a quote is drafted against a proposal, which is what names its opportunity. */
    createQuoteForProposal: (proposalId: string, body: QuotePayload, key: string) =>
      c.post<Quote>(`/v1/proposals/${seg(proposalId)}/quotes`, body, w(key)),
    addQuoteVersion: (quoteId: string, body: QuotePayload, key: string) =>
      c.post<QuoteVersion>(`/v1/quotes/${seg(quoteId)}/versions`, body, w(key)),
    /** SR-018: the approval is a fact about one version and is never carried into `n + 1`, so `version` is named. */
    approveQuote: (quoteId: string, version: number, approval_ref: string, key: string) =>
      c.post<QuoteVersion>(`/v1/quotes/${seg(quoteId)}/approve`, { version, approval_ref }, w(key)),
    /**
     * 7.5.b's rejection: the version named, and why it was refused.
     *
     * The same decision as the proposal's rejection, on the pricing side of the same desk, and it names the version
     * for the same reason the approval does — a decision is a fact about one version and is never carried into `n + 1`.
     */
    rejectQuote: (quoteId: string, version: number, reason: string, key: string) =>
      c.post<QuoteVersion>(`/v1/quotes/${seg(quoteId)}/reject`, { version, reason }, w(key)),
    /**
     * 7.5.b's validity: the store's own reading of the window, for the whole chain.
     *
     * The page does not compute this. The window is the store's rule (`hasLapsed`), and the send gate and the expiry
     * sweep act on it — so a badge read from here cannot disagree with the gate that would refuse.
     */
    readQuoteValidity: (quoteId: string, p: { at?: string } = {}, signal?: AbortSignal) =>
      c.get<QuoteValidity>(`/v1/quotes/${seg(quoteId)}/valid${query({ at: p.at })}`, opt(signal)),
    sendQuote: (quoteId: string, key: string) => c.post<QuoteVersion>(`/v1/quotes/${seg(quoteId)}/send`, {}, w(key)),

    // Pricing scenarios (AS-006 §Pricing). A variant is a sibling carrying `supersedes`, never an edit.
    listPricingScenarios: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: PricingScenario[] }>(`/v1/opportunities/${seg(opportunityId)}/pricing-scenarios${query({ limit: p.limit })}`, opt(signal)),
    createPricingScenario: (opportunityId: string, body: ScenarioCreateBody, key: string) =>
      c.post<PricingScenario>(`/v1/opportunities/${seg(opportunityId)}/scenarios`, body, w(key)),
    addScenarioVariant: (scenarioId: string, body: ScenarioVariantBody, key: string) =>
      c.post<PricingScenario>(`/v1/scenarios/${seg(scenarioId)}/variants`, body, w(key)),
    /** AR-008: promotion is the gate, and `promoted_at` is terminal — the scenario a quote was built from is never overwritten. */
    promoteScenario: (scenarioId: string, key: string) => c.post<PricingScenario>(`/v1/scenarios/${seg(scenarioId)}/approve`, {}, w(key)),
    /** The comparator is opportunity-scoped: the route resolves the scenario's own opportunity first, so `against` is not a parameter. */
    compareScenarios: (scenarioId: string, signal?: AbortSignal) =>
      c.get<{ data: ScenarioComparisonRow[] }>(`/v1/scenarios/${seg(scenarioId)}/compare`, opt(signal)),

    // Contracts. References rather than the documents themselves (ADR-009).
    listContracts: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: Contract[] }>(`/v1/opportunities/${seg(opportunityId)}/contracts${query({ limit: p.limit })}`, opt(signal)),
    /**
     * 7.7.c's template validity: the registry's answer for the pinned template version (F-014).
     *
     * The read is `sales.contract:read`, which the seller's `sales.*:read` glob reaches and which a caller without
     * it is refused — so a page may show the pin to everybody and this verdict only to those who may read the
     * registry, and it must not guess the verdict from the version.
     */
    readContractTemplateValidity: (contractId: string, signal?: AbortSignal) =>
      c.get<ContractTemplateValidity>(`/v1/contracts/${seg(contractId)}/template-validity`, opt(signal)),
    draftContract: (opportunityId: string, body: ContractDraftBody, key: string) =>
      c.post<Contract>(`/v1/opportunities/${seg(opportunityId)}/contracts`, body, w(key)),
    sendContract: (contractId: string, key: string) => c.post<Contract>(`/v1/contracts/${seg(contractId)}/send`, {}, w(key)),
    /**
     * F-015: the e-signature platform's at-least-once delivery, so a replayed event is a no-op rather than a second
     * signatory. `signed_at` is deliberately not a parameter — the store re-stamps it from its own clock.
     */
    recordSignatureEvent: (contractId: string, body: { name: string; role: string }, key: string) =>
      c.post<Contract>(`/v1/contracts/${seg(contractId)}/signature-events`, body, w(key)),
    /** ADR-008's binding close criterion: the executed artefact's hash, locked once and never moved. */
    executeContract: (contractId: string, customer_artifact_hash: string, key: string) =>
      c.post<Contract>(`/v1/contracts/${seg(contractId)}/execute`, { customer_artifact_hash }, w(key)),
    supersedeContract: (contractId: string, superseded_by: string, key: string) =>
      c.post<{ predecessor: Contract; successor: Contract }>(`/v1/contracts/${seg(contractId)}/supersede`, { superseded_by }, w(key)),

    /**
     * SR-094 / SR-095: the field-by-field handoff validator. It answers a verdict rather than throwing on both
     * outcomes — an incomplete handoff is a legitimate thing for a seller to be holding — so a panel reads
     * `missing[]` off a `200`. `validator_run_id` is the caller's own run id, so a retried validation is
     * recognisable as the same run rather than a second verdict about the same opportunity.
     */
    validateHandoff: (opportunityId: string, validator_run_id: string, key: string) =>
      c.post<HandoffValidation>(`/v1/opportunities/${seg(opportunityId)}/handoff/validate`, { validator_run_id }, w(key)),

    // Package composability (AS-002). One Solution, one Proposal version, one Quote and one Contract per product.
    packageCoverage: (opportunityId: string, p: { at?: string } = {}, signal?: AbortSignal) =>
      c.get<PackageCoverage>(`/v1/opportunities/${seg(opportunityId)}/package/coverage${query({ at: p.at })}`, opt(signal)),
    /** `{ data: null }` for a deal whose products match no catalog bundle: "this deal is not a bundle" is an answer. */
    packageBundle: (opportunityId: string, signal?: AbortSignal) =>
      c.get<{ data: PackageBundle | null }>(`/v1/opportunities/${seg(opportunityId)}/package/bundle`, opt(signal)),
    packageApprovals: (opportunityId: string, signal?: AbortSignal) =>
      c.get<PackageApprovals>(`/v1/opportunities/${seg(opportunityId)}/package/approvals`, opt(signal)),
    packageEvents: (opportunityId: string, signal?: AbortSignal) =>
      c.get<PackageEventStreams>(`/v1/opportunities/${seg(opportunityId)}/package/events`, opt(signal)),

    // Location rollout (AS-003 / SR-048). On the Sales side the Onboarding record IS the Opportunity, so `:id`
    // here is the opportunity id the store keys every row by.
    planLocations: (opportunityId: string, body: LocationPlanBody, key: string) =>
      c.post<LocationRolloutPlan>(`/v1/onboardings/${seg(opportunityId)}/locations/plan`, body, w(key)),
    setLocationStatus: (opportunityId: string, locationId: string, body: LocationStatusBody, key: string) =>
      c.post<LocationRolloutOutcome>(`/v1/onboardings/${seg(opportunityId)}/locations/${seg(locationId)}/status`, body, w(key)),
    locationStatus: (opportunityId: string, signal?: AbortSignal) =>
      c.get<{ data: LocationRollout[] }>(`/v1/onboardings/${seg(opportunityId)}/locations`, opt(signal)),
    locationsAtRisk: (opportunityId: string, signal?: AbortSignal) =>
      c.get<{ data: AtRiskLocation[] }>(`/v1/onboardings/${seg(opportunityId)}/locations/at-risk`, opt(signal)),

    // Competitors (AS-020 / AR-013). A rejected record stays in the listing: a rejection is a decision, not an erase.
    recordCompetitor: (opportunityId: string, body: RecordCompetitorBody, key: string) =>
      c.post<CompetitorRecord>(`/v1/opportunities/${seg(opportunityId)}/competitors`, body, w(key)),
    listCompetitors: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: CompetitorRecord[] }>(`/v1/opportunities/${seg(opportunityId)}/competitors${query({ limit: p.limit })}`, opt(signal)),
    approveCompetitor: (competitorId: string, key: string) =>
      c.post<CompetitorDecision>(`/v1/competitors/${seg(competitorId)}/approve`, {}, w(key)),
    rejectCompetitor: (competitorId: string, reason: string, key: string) =>
      c.post<CompetitorDecision>(`/v1/competitors/${seg(competitorId)}/reject`, { reason }, w(key)),
    /**
     * The screening pass `sales-intelligence` runs: a POST because the candidate list travels in the body, and it
     * reads no store and writes nothing — so the key it carries is one the client generated, not the caller's.
     */
    deriveBlinkers: (opportunityId: string, body: BlinkerDeriveBody, signal?: AbortSignal) =>
      c.post<{ data: BlinkerCandidate[] }>(`/v1/opportunities/${seg(opportunityId)}/blinkers`, body, { signal, idempotencyKey: newIdempotencyKey() }),

    // Cross-sell (ADR-015 / F-044). Every route here is the Account owner's; the active deal is read-only on all of them.
    surfaceCrossSell: (accountId: string, body: CrossSellSurfaceBody, key: string) =>
      c.post<CrossSellRecommendation>(`/v1/accounts/${seg(accountId)}/cross-sell-signals`, body, w(key)),
    /** Every status by default: a suppressed recommendation is still the owner's to see, and hiding it hides why. */
    listCrossSellForAccount: (accountId: string, p: { limit?: number; status?: CrossSellStatus } = {}, signal?: AbortSignal) =>
      c.get<{ data: CrossSellRecommendation[] }>(`/v1/accounts/${seg(accountId)}/cross-sell-signals${query({ limit: p.limit, status: p.status })}`, opt(signal)),
    dismissCrossSell: (accountId: string, signalId: string, reason: string, key: string) =>
      c.post<CrossSellRecommendation>(`/v1/accounts/${seg(accountId)}/cross-sell-signals/${seg(signalId)}/dismiss`, { reason }, w(key)),
    /** The link is recorded after the fact: this names a deal `expansion.ts` already created. */
    actionCrossSell: (accountId: string, signalId: string, expansion_id: string, key: string) =>
      c.post<CrossSellRecommendation>(`/v1/accounts/${seg(accountId)}/cross-sell-signals/${seg(signalId)}/actioned`, { expansion_id }, w(key)),

    // Expansion (SR-022 / AS-025). No pipeline stage moves here: the store refuses anything else.
    createExpansionOpportunity: (accountId: string, body: ExpansionCreateBody, key: string) =>
      c.post<ExpansionOpportunity>(`/v1/accounts/${seg(accountId)}/expansion-opportunities`, body, w(key)),
    listExpansionOpportunities: (accountId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: ExpansionOpportunity[] }>(`/v1/accounts/${seg(accountId)}/expansion-opportunities${query({ limit: p.limit })}`, opt(signal)),
    updateExpansionOpportunity: (expansionId: string, body: ExpansionUpdateBody, key: string) =>
      c.put<ExpansionOpportunity>(`/v1/expansion-opportunities/${seg(expansionId)}`, body, w(key)),
    /** The owner has picked the recommendation up: a write of `updated_at` and an audit row, nothing else. */
    touchExpansionOpportunity: (expansionId: string, key: string) =>
      c.post<ExpansionOpportunity>(`/v1/expansion-opportunities/${seg(expansionId)}/touch`, {}, w(key)),

    // Activation (SR-021 / SR-053 / AS-013). Per `(opportunity, product)`: a bundle's products go live on different days.
    evaluateActivation: (opportunityId: string, body: ActivationEvaluateBody, key: string) =>
      c.post<Activation>(`/v1/opportunities/${seg(opportunityId)}/activations/evaluate`, body, w(key)),
    confirmActivation: (opportunityId: string, body: ActivationConfirmBody, key: string) =>
      c.post<ActivationConfirmation>(`/v1/opportunities/${seg(opportunityId)}/activations/confirm`, body, w(key)),
    completeOnboarding: (opportunityId: string, body: { source_event_id: string; completed_at?: string }, key: string) =>
      c.post<ActivationCompleted>(`/v1/opportunities/${seg(opportunityId)}/activations/onboarding-completed`, body, w(key)),
    /** Only the confirmed subset, sorted by `product_id` in byte order: a UI diffing two reads gets a stable answer. */
    confirmedProducts: (opportunityId: string, signal?: AbortSignal) =>
      c.get<{ data: Activation[] }>(`/v1/opportunities/${seg(opportunityId)}/activations/confirmed-products`, opt(signal)),
    /**
     * The stored activation for ONE product of one deal, and `null` when Onboarding has never evaluated that
     * product. `GET /v1/opportunities/:id/activations/:product_id` answers `notFound` for a pair the store holds
     * nothing for, which is "not evaluated yet" rather than a failure to read — so it is answered here, the way
     * `getHierarchy` answers an account with no record. `confirmedProducts` cannot stand in: it returns the
     * confirmed subset alone, so a `ready` record and a record that was never written read identically.
     */
    activationForProduct: async (opportunityId: string, productId: string, signal?: AbortSignal): Promise<Activation | null> => {
      try {
        return await c.get<Activation>(`/v1/opportunities/${seg(opportunityId)}/activations/${seg(productId)}`, opt(signal));
      } catch (e) {
        if (asApiFailure(e)?.status === 404) return null;
        throw e;
      }
    },
    activationFollowUp: (opportunityId: string, p: { product_id?: string } = {}, signal?: AbortSignal) =>
      c.get<ActivationFollowUp>(`/v1/opportunities/${seg(opportunityId)}/activations/follow-up${query({ product_id: p.product_id })}`, opt(signal)),
    /** A product with no definition is a `notFound` from the store, not an empty object. */
    activationDefinition: (productId: string, signal?: AbortSignal) =>
      c.get<ActivationDefinition>(`/v1/activation-definitions/${seg(productId)}`, opt(signal)),

    // Activation failures (F-020 / F-021). None of these moves an Opportunity; `planMove` stays the only thing that may.
    detectActivationFailure: (opportunityId: string, key: string) =>
      c.post<SlipDetection>(`/v1/opportunities/${seg(opportunityId)}/activation-failures/detect`, {}, w(key)),
    /** F-021: the store returns the `closed_lost` transition `planMove` must be asked for; it does not close the deal. */
    cancelActivation: (opportunityId: string, body: CancelActivationBody, key: string) =>
      c.post<PreActivationCancellationOutcome>(`/v1/opportunities/${seg(opportunityId)}/activation-failures/cancel`, body, w(key)),
    escalateActivationFailure: (failureId: string, body: { assigned_to?: string } = {}, key?: string) =>
      c.post<EscalationOutcome>(`/v1/activation-failures/${seg(failureId)}/escalate`, body, w(key ?? newIdempotencyKey())),
    resolveActivationFailure: (failureId: string, key: string) =>
      c.post<ActivationFailure>(`/v1/activation-failures/${seg(failureId)}/resolve`, {}, w(key)),
    listActivationFailures: (opportunityId: string, signal?: AbortSignal) =>
      c.get<{ data: ActivationFailure[] }>(`/v1/opportunities/${seg(opportunityId)}/activation-failures`, opt(signal)),

    // Processor change (AS-004 / F-009 / SR-048). A fact about the deal, and the deal's own artefacts it supersedes.
    captureProcessorChange: (opportunityId: string, body: ProcessorChangeBody, key: string) =>
      c.post<ProcessorChange>(`/v1/opportunities/${seg(opportunityId)}/processor-changes`, body, w(key)),
    linkProcessorChange: (processorChangeId: string, body: ProcessorChangeLinksBody, key: string) =>
      c.post<ProcessorChange>(`/v1/processor-changes/${seg(processorChangeId)}/link`, body, w(key)),
    supersedeProcessorChange: (processorChangeId: string, key: string) =>
      c.post<ProcessorChangeSupersession>(`/v1/processor-changes/${seg(processorChangeId)}/supersede`, {}, w(key)),
    listProcessorChanges: (opportunityId: string, p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: ProcessorChange[] }>(`/v1/opportunities/${seg(opportunityId)}/processor-changes${query({ limit: p.limit })}`, opt(signal)),
    /** The current processor of record is the latest entry's **id**, so the answer is `{ data: <id> | null }`. */
    latestProcessorChange: (opportunityId: string, signal?: AbortSignal) =>
      c.get<{ data: string | null }>(`/v1/opportunities/${seg(opportunityId)}/processor-changes/latest`, opt(signal)),

    // -------------------------------------------------------------------------------------------
    // Phase 6, Sales operations. Only the routes `src/api.ts` actually registers are written here: a page whose
    // read has no route behind it says so on its own page rather than being pointed at a URL that answers 404.
    // The routing desk has all three of its calls (tier 6.2.6), the territory grid has its three (tier 6.3.5),
    // and the approval inbox has its listing (tier 6.5.7).
    // -------------------------------------------------------------------------------------------

    /**
     * Tier 6.2.6 — the routing desk's queue: `GET /v1/routing/queue`, the accounts waiting on a routing
     * decision.
     *
     * The route answers `{ data: RoutingQueueRow[] }`, and the row is the one the page reads: the parked lead,
     * the account it was raised against and the automation decision that escalated it. The read writes nothing,
     * so asking for it twice leaves the platform byte-identical.
     */
    routingQueue: (p: { limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: RoutingQueueRow[] }>(`/v1/routing/queue${query({ limit: p.limit })}`, opt(signal)),

    /**
     * I-3: the UI MAY ask the engine what it would do, and `POST /v1/routing/preview` is that door. The route
     * answers with the engine's own decision, so the page shows the rule that matched and the tie-breaker that
     * refused a candidate — it derives neither.
     *
     * The key is a fresh one per call: a preview is a question, not a write, and two questions about the same
     * account with different candidates must not answer each other.
     */
    previewRouting: (body: RoutingPreviewBody, signal?: AbortSignal) =>
      c.post<{ data: RoutingDecision }>("/v1/routing/preview", body, { signal, idempotencyKey: newIdempotencyKey() })
        .then((r) => r.data),

    /**
     * `POST /v1/routing/assign` — the write, and the only way an account is placed by hand.
     *
     * The route answers with the engine's own result (`{ decision, result, trace_id }`) and the page reads a flat
     * assignment, so the mapping is done here rather than in the page. `replayed` is the one field the route does
     * not carry: a repeated request under the same `Idempotency-Key` is answered with the stored response and the
     * header that says so, and the client cannot read that header — so the client does not claim a replay it
     * cannot see.
     */
    assignRouting: (body: RoutingAssignBody, idempotencyKey: string) =>
      c.post<{ data: RoutingAssignAnswer }>("/v1/routing/assign", body, w(idempotencyKey)).then((r) => ({
        account_id: r.data.result.assignment.account_id,
        owner_id: r.data.result.assignment.owner_id,
        assigned_by: r.data.result.assignment.assigned_by,
        assigned_at: r.data.result.assignment.assigned_at,
        rule_id_used: r.data.result.assignment.rule_id_used,
        priority: r.data.result.assignment.priority,
        trace_id: r.data.trace_id,
        replayed: false,
      } satisfies RoutingAssignment)),

    /**
     * Tier 6.3.5 — the territory grid: `GET /v1/territories`, the definitions with the accounts each one matches.
     *
     * The route answers the listing as `src/territories.ts` holds it, and the one field the page reads differently
     * is mapped here rather than in the page: the record carries `effective_to` **absent** while the definition is
     * live, and the page reads `null`. The mapping is the client's job, so the page draws one shape rather than
     * two.
     */
    listTerritories: (p: { active_only?: boolean } = {}, signal?: AbortSignal) =>
      c.get<{ data: (Omit<TerritoryDefinition, "effective_to"> & { effective_to?: string })[] }>(
        // The route's own query schema types `active_only` as a boolean, so it travels as one — the shared
        // `query` helper writes strings, and `false` is the one value that must not become `"false"`.
        `/v1/territories${query({ active_only: p.active_only === undefined ? undefined : String(p.active_only) })}`, opt(signal),
      ).then((r) => ({ data: r.data.map((t) => ({ ...t, effective_to: t.effective_to ?? null })) })),

    /**
     * `GET /v1/accounts/:account_id/territory` — the assignment ledger for one account (`src/territory.ts`). The
     * rows are the audit chain rather than the live pointer: the account's own route never answers with a
     * superseded row, so the one row here is the one that still holds the account.
     */
    listAccountAssignments: (accountId: string, signal?: AbortSignal) =>
      c.get<{ data: TerritoryAssignment[] }>(`/v1/accounts/${seg(accountId)}/territory`, opt(signal)),

    /**
     * `POST /v1/territories/:territory_id/deactivate` — closes the definition and re-evaluates what it matched.
     * The answer carries the day it stopped and how many accounts it held, so the confirmation names the count
     * the server counted rather than one the page guessed before the call.
     */
    deactivateTerritory: (territoryId: string, key: string) =>
      c.post<TerritoryDeactivation>(`/v1/territories/${seg(territoryId)}/deactivate`, {}, w(key)),

    /**
     * Tier 6.5.7 — the approval inbox's listing: `GET /v1/approvals`, the requests the desk works through.
     *
     * The route answers `{ data: Approval[] }` — the record as `src/approvals.ts` holds it — and the page draws
     * the subject, the requester, the chain, the step, the status, the threshold, the window and the note from
     * it without deriving any of them. The listing is a read: asking for it twice leaves the platform
     * byte-identical.
     *
     * `status` narrows the listing to one status and `limit` caps it; the client sends neither unless the
     * caller names one, so the inbox asks for the whole list by default — a waiting request and a decided one
     * are both on it, and the page orders them.
     */
    listApprovals: (p: { status?: ApprovalStatus; limit?: number } = {}, signal?: AbortSignal) =>
      c.get<{ data: Approval[] }>(`/v1/approvals${query({ status: p.status, limit: p.limit })}`, opt(signal)),

    /**
     * SR-037 / SR-038, tier 6.10.5: `GET /v1/audit/entries`, the field-level trail. It is named `audit` rather than
     * `auditEntries` because `AuditLogPage` picks exactly that call off this client and says so on the page when it
     * is absent. The route answers the page at the top level, not under `data`, and it never supplies a default
     * filter — `src/audit.ts` refuses an unbounded read, so an unfurnished query is a refusal and not an empty trail.
     */
    audit: (q: AuditQuery, signal?: AbortSignal) => c.get<AuditPage>(`/v1/audit/entries${query(auditParams(q))}`, opt(signal)),

    /**
     * Tier 6.8.6 — the forecast desk's periods: `GET /v1/forecast-period-snapshots/periods`, the published
     * snapshots grouped by the period they belong to.
     *
     * The route answers the group as the store holds it — `period_start`, `period_end`, the period's own name
     * (`period`) and the snapshots — and this method hands the page the shape it reads. The one field the
     * mapping exists for is `period`: the label is the period's identity (`2026-W42`, `2026-10`, `2026-Q4`),
     * and the store derives it from its own `periodFor`, so the name on screen is the name the store gave it.
     *
     * The page reads the **snapshot** and never the live `forecast_category` (I-4, SR-078): the frozen copy is
     * the claim, and a live roll-up beside it is the reconciliation note.
     */
    listForecastPeriods: (signal?: AbortSignal) =>
      c.get<{ data: ({ period_start: string; period_end: string; period?: SnapshotPeriod; snapshots: ForecastPeriodRead["snapshots"] })[] }>(
        "/v1/forecast-period-snapshots/periods", opt(signal),
      ).then((r) => r.data.map((g) => ({
        period: g.period ?? { label: g.period_start, start: g.period_start, end: g.period_end },
        snapshots: g.snapshots,
      }))),

    /**
     * Tier 6.2: `GET /v1/quota/users/:user_id`, the governing plan versions and the targets in force for one
     * seller and one period. `period` is the store's own label (`2026`, `2026-H2`, `2026-Q3`), not a date range,
     * and the route requires it — there is no default period to fall back on.
     */
    readQuota: (userId: string, period: string, o: { signal?: AbortSignal } = {}) =>
      c.get<{ data: QuotaRead }>(`/v1/quota/users/${seg(userId)}${query({ period })}`, opt(o.signal)).then((r) => r.data),

    /**
     * Tier 6.2: `GET /v1/quota/users/:user_id/attainment`, the store's own computation for one seller, one period
     * and optionally one product. `product` narrows the read to a single product rather than filtering a whole-period
     * answer, which is what keeps another product's target off the desk.
     */
    computeAttainment: (userId: string, period: string, o: { signal?: AbortSignal; product?: string } = {}) =>
      c.get<{ data: Attainment }>(`/v1/quota/users/${seg(userId)}/attainment${query({ period, product: o.product })}`, opt(o.signal)).then((r) => r.data),

    // -------------------------------------------------------------------------------------------
    // Tier 1.6 — the two Sales-Operations surfaces `src/routes/audit-export.ts` and
    // `src/routes/access-review.ts` register. Both are declared on a `:read` permission because that is
    // the only action the compiled policy resolves for an operational role (`:write`/`:export` resolve for
    // `gweb_admin` alone), and both hold their real ceiling in the store underneath: a caller below it is
    // refused with a `403` and audited, rather than answered with no rows. So a `403` from any of these
    // six routes means "your role does not reach this", not "there was nothing there" — the pages draw the
    // two differently and neither is a failure to fix here.
    // -------------------------------------------------------------------------------------------

    /**
     * SR-119 / AR-022, tier 1.6: `POST /v1/audit/exports` (`sales.audit:read`), the bundle over a stated
     * interval. One call answers `201` with the whole bundle, so there is no paging and no cursor: a bundle
     * is a file, and a screen is not the file.
     *
     * The route is idempotent in the transport's sense, and the key is minted here rather than taken as a
     * parameter — an export is not a replayable write of a business record, so there is no logical action a
     * caller could name twice. A fresh key per call makes every call a genuine re-export, which is the half
     * of SR-119 §Lifecycle that asks for a fresh signature: the older bundle is preserved and its successor
     * chains onto it. A repeated key would instead replay one stored bundle and append no second chain link,
     * which is the half §Acceptance criteria asks for and is available to a caller that holds a key.
     *
     * The body travels exactly as the page stated it. `reason` is not sent because the page has no field for
     * it, and SR-119's `fields` selection is not sent because the route refuses it with an audited `403`.
     */
    exportAudit: (body: AuditExportRequest, signal?: AbortSignal) =>
      c.post<AuditExportBundle>("/v1/audit/exports", body, { signal, idempotencyKey: newIdempotencyKey() }),

    /**
     * `GET /v1/audit/export/latest` (`sales.audit:read`) — the most recent manifest in the scope the server
     * holds, which is why it takes no scope: the caller does not know it.
     *
     * A scope with no export yet is a `404` on this route, and that is an answer rather than a failure — the
     * same shape `getHierarchy` turns into `null` — so it is turned into `null` here and the page says the
     * chain has no prior link. Any other status still throws, so a refused read is never drawn as an empty
     * scope.
     */
    lastAuditExport: async (signal?: AbortSignal): Promise<AuditExportManifest | null> => {
      try {
        return (await c.get<{ data: AuditExportManifest }>("/v1/audit/export/latest", opt(signal))).data;
      } catch (e) {
        if (asApiFailure(e)?.status === 404) return null;
        throw e;
      }
    },

    /**
     * `POST /v1/audit/export/verify` (`sales.audit:read`) — the one check a browser cannot make: the chain
     * link is HMAC-SHA256 over a key the platform never gives out, so a reviewer is told whether the chain
     * holds rather than shown a hash to trust. The store reads a list of pages and this bundle is one of
     * them, hence the `pages` wrapper; the route answers `{ ok, breaks }` under `data`, and a detected
     * break is a `200` with the indices named — an answer, not a server failure.
     *
     * The route is not `idempotent` and computes nothing twice, but the transport still wants a key on every
     * POST, so one is minted per call and nothing is replayed with it.
     */
    verifyAuditExport: (bundle: AuditExportBundle, signal?: AbortSignal) =>
      c.post<{ data: AuditExportChainResult }>("/v1/audit/export/verify", { pages: [bundle] }, { signal, idempotencyKey: newIdempotencyKey() }).then((r) => r.data),

    /**
     * SR-114, tier 1.6: `GET /v1/access-reviews` (`sales.access:read`), the projection over one window. Both
     * bounds are sent because the route requires both and has no default: a review whose window nobody chose
     * is not a review of anything, and a default invented here would be a window the caller believed it
     * picked.
     *
     * The route answers `{ data, from_iso, to_iso }`, echoing the window beside the rows, so this returns
     * `data` and the window stays the caller's own to put on screen — `ReviewPeriod` is what the page asked
     * about, not a value re-read off a response that could disagree with it.
     */
    accessReviewRows: (period: ReviewPeriod, signal?: AbortSignal) =>
      c.get<{ data: AccessReviewRow[]; from_iso: string; to_iso: string }>(`/v1/access-reviews${query({ from: period.from_iso, to: period.to_iso })}`, opt(signal)).then((r) => r.data),

    /**
     * `POST /v1/access-reviews/run` (`sales.access:read`) — the quarterly run over the cadence that has just
     * elapsed, audited as `access_review.run.v1`. It takes no window and none is sent: a caller-chosen window
     * for a *run* would be a second, unstated cadence. The whole report is answered rather than a count, so a
     * reviewer holding `report_id` can show what was decided at the time.
     *
     * The route is deliberately not idempotent — the store mints a fresh `report_id` and writes an audit row
     * on every call — so the key minted here is carried for symmetry and nothing is replayed under it.
     */
    runAccessReview: (signal?: AbortSignal) =>
      c.post<AccessReviewRun>("/v1/access-reviews/run", {}, { signal, idempotencyKey: newIdempotencyKey() }),

    /**
     * `POST /v1/access-reviews/decisions` (`sales.access:read`) — the reviewer's own verdict on one
     * `(subject_id, role)` row, audited as `access_review.decision.v1`. It is the one method in this group whose
     * key comes from the caller rather than being minted here, because a decision is a decision a person takes
     * and the page holds one key per form for exactly that reason.
     *
     * Recording `revoked` does not revoke anything: it writes the verdict against the row and leaves the
     * grant in force. The body is the store's own `AccessReviewDecision`, all four fields required, so a
     * refusal is the store's `422` naming the field it refused rather than a shape this method could invent.
     */
    recordAccessReviewDecision: (body: AccessReviewDecision, key: string) =>
      c.post<{ data: { subject_id: string; role: string; review_status: string } }>("/v1/access-reviews/decisions", body, w(key)),
  };
}
export type SalesApi = ReturnType<typeof makeApi>;
/** The one call the hierarchy panel needs, so it can be read against a fake as well as against the app's client. */
export type HierarchyApi = Pick<SalesApi, "getHierarchy">;

// ---------------- Metrics ----------------

/**
 * How far back a panel looks. The dashboard asks for one of these three and nothing else: a wider window is a
 * different question, not a bigger number on the same card.
 */
export const METRIC_WINDOWS = ["1h", "24h", "7d"] as const;
export type MetricWindow = (typeof METRIC_WINDOWS)[number];

/** The look-back each window means, so the UI and the server never disagree about what "24h" covers. */
export const METRIC_WINDOW_MS: Record<MetricWindow, number> = {
  "1h": 3_600_000, "24h": 86_400_000, "7d": 604_800_000,
};

/** Five seconds is often enough to watch a stage transition, and slow enough not to be a firehose. */
export const DEFAULT_POLL_INTERVAL_MS = 5_000;
/** A poll faster than this is a hot loop against the API, so it is raised rather than refused. */
const MIN_POLL_INTERVAL_MS = 250;

/**
 * One reading of one metric, in the spelling the panels read: the metric name, the frozen label set from
 * `src/metrics.ts`, the value and when it was seen.
 */
export interface MetricsEvent {
  name: string;
  labels: Record<string, string>;
  value: number;
  timestamp: string;
}

/**
 * One read of one metric over a window.
 *
 * Phase 7's commercial surface did land: `src/api.ts` registers 227 routes, including every Phase 7 method in
 * `makeApi` above. `GET /v1/metrics/:name` is the exception and is still not one of them — nothing under `src/`
 * registers a metrics read — so this stays the contract the UI is written against rather than a feature that
 * exists, and a panel that mounts it polls a route that answers 404.
 */
export interface MetricsClient {
  read(name: string, o: { since?: string; until?: string; signal?: AbortSignal }): Promise<MetricsEvent[]>;
}

export interface OpenMetricsStreamOptions {
  /** The metric name exactly as `src/metrics.ts` spells it, dots included. */
  name: string;
  /** ISO instants bounding the read. Absent means the route decides. */
  since?: string;
  until?: string;
  poll_interval_ms?: number;
}

/**
 * Reads a metric over the platform client, so it carries the same token, timeout and error envelope as every other
 * call. The list reads answer as `{ data: [...] }`; a bare array is accepted too, so the route is free to pick either.
 */
export function makeMetricsClient(c: ApiClient): MetricsClient {
  return {
    async read(name, { since, until, signal }) {
      const body = await c.get<unknown>(`/v1/metrics/${seg(name)}${query({ since, until })}`, { signal });
      const rows = Array.isArray(body)
        ? body
        : Array.isArray((body as { data?: unknown } | null)?.data) ? (body as { data: unknown[] }).data : [];
      return rows.map(toMetricsEvent).filter((e): e is MetricsEvent => e !== null);
    },
  };
}

/** `timeUnixNano` as the emitter writes it, or an ISO string, turned into the ISO string the panel sorts by. */
const isoFromNanos = (raw: unknown): string | null => {
  const nanos = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : Number.NaN;
  const date = new Date(nanos / 1e6);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * Accepts either spelling of a datapoint: the `MetricsEvent` a read route would answer, or the OTLP line
 * `src/metrics.ts` writes (`metric`, `timeUnixNano`, `labels`, `value`). A record with no name or with a value that is
 * not finite is dropped, exactly as the emitter drops what it cannot write, so a panel never draws a number that
 * nothing observed. A record with no readable time is kept with the epoch, because the value is still a value.
 */
function toMetricsEvent(raw: unknown): MetricsEvent | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const name = typeof r.name === "string" ? r.name : typeof r.metric === "string" ? r.metric : null;
  const value = typeof r.value === "number" ? r.value : Number(r.value);
  if (!name || !Number.isFinite(value)) return null;
  const source = typeof r.labels === "object" && r.labels !== null ? (r.labels as Record<string, unknown>) : {};
  const labels = Object.fromEntries(Object.entries(source).map(([k, v]) => [k, String(v)]));
  const timestamp = typeof r.timestamp === "string" ? r.timestamp : isoFromNanos(r.timeUnixNano ?? r.time);
  return { name, labels, value, timestamp: timestamp ?? new Date(0).toISOString() };
}

/** The session token, read the way `@gweb/ui` writes it: this tab only. */
const SESSION_TOKEN_KEY = "gweb.session.token";

/** Same origin, so the dev proxy and a deployment's ingress both reach /v1 without the panel knowing either exists. */
function ambientMetricsClient(): MetricsClient {
  return (ambient ??= makeMetricsClient(createApiClient({
    baseUrl: "",
    getToken: () => (typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(SESSION_TOKEN_KEY)),
  })));
}
let ambient: MetricsClient | undefined;

/**
 * Polls one metric and yields each record as it arrives. This is not a server push: the route answers a snapshot, so
 * the panel re-reads on an interval and sees the window move.
 *
 * The iterator is close-able. `return()` — which is what leaving a `for await` loop does — clears the interval,
 * aborts the read in flight, and wakes anything waiting, so an unmounted panel stops asking. A poll that fails is not
 * the end of the stream: it is skipped, and the next interval tries again, because a dropped read is not a metric that
 * stopped being emitted.
 */
export function openMetricsStream(
  opts: OpenMetricsStreamOptions, client: MetricsClient = ambientMetricsClient(),
): AsyncIterableIterator<MetricsEvent> {
  const every = Math.max(MIN_POLL_INTERVAL_MS, opts.poll_interval_ms ?? DEFAULT_POLL_INTERVAL_MS);
  const buffered: MetricsEvent[] = [];
  const waiting: ((r: IteratorResult<MetricsEvent>) => void)[] = [];
  const inFlight = new AbortController();
  let closed = false;

  const poll = async (): Promise<void> => {
    if (closed) return;
    try {
      const events = await client.read(opts.name, { since: opts.since, until: opts.until, signal: inFlight.signal });
      if (closed) return;
      for (const event of events) {
        const waiter = waiting.shift();
        if (waiter) waiter({ value: event, done: false });
        else buffered.push(event);
      }
    } catch {
      // A read that fails is skipped, not fatal: the panel keeps the numbers it already had.
    }
  };

  const timer = setInterval(() => { void poll(); }, every);
  void poll();

  const close = (): Promise<IteratorResult<MetricsEvent>> => {
    if (!closed) {
      closed = true;
      clearInterval(timer);
      inFlight.abort();
    }
    for (const waiter of waiting.splice(0)) waiter({ value: undefined, done: true });
    return Promise.resolve({ value: undefined, done: true });
  };

  return {
    [Symbol.asyncIterator](): AsyncIterableIterator<MetricsEvent> { return this; },
    async next(): Promise<IteratorResult<MetricsEvent>> {
      const ready = buffered.shift();
      if (ready) return { value: ready, done: false };
      if (closed) return { value: undefined, done: true };
      return new Promise<IteratorResult<MetricsEvent>>((resolve) => waiting.push(resolve));
    },
    return: close,
    async throw(e?: unknown): Promise<IteratorResult<MetricsEvent>> {
      await close();
      throw e;
    },
  };
}

/** Reads pages until there are no more, or the cap is reached. The cap keeps one screen from loading without end. */
export async function fetchAll<T>(load: (after: string | null) => Promise<Page<T>>, maxPages = 5): Promise<{ items: T[]; truncated: boolean }> {
  const items: T[] = [];
  let after: string | null = null;
  for (let i = 0; i < maxPages; i++) {
    const page: Page<T> = await load(after);
    items.push(...page.data);
    if (!page.has_more || !page.next_after) return { items, truncated: false };
    after = page.next_after;
  }
  return { items, truncated: true };
}
